var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
const keyboardControls = {
    back: 'BACK',
    channelDown: 'CHANNELDOWN',
    channelUp: 'CHANNELUP',
    directionDown: 'DOWN',
    directionEnter: 'SELECT',
    directionLeft: 'LEFT',
    directionRight: 'RIGHT',
    directionUp: 'UP',
    exit: 'EXIT',
    guide: 'GUIDE',
    info: 'INFO',
    key0: 'NUM0',
    key1: 'NUM1',
    key2: 'NUM2',
    key3: 'NUM3',
    key4: 'NUM4',
    key5: 'NUM5',
    key6: 'NUM6',
    key7: 'NUM7',
    key8: 'NUM8',
    key9: 'NUM9',
    keyDash: 'ADVANCE',
    keyEnter: 'ENTER',
    mediaPlayerForward: 'FORWARD',
    mediaPlayerPause: 'PAUSE',
    mediaPlayerPlay: 'PLAY',
    mediaPlayerRecord: 'RECORD',
    mediaPlayerReverse: 'REVERSE',
    mediaPlayerSkipAhead: 'ADVANCE',
    mediaPlayerSkipBack: 'REPLAY',
    menu: 'TIVO',
    pageDown: 'CHANNELDOWN',
    pageUp: 'CHANNELUP',
};
// Lookup for channel Favorite entry including navigation keys to support 'shortcuts' such
// as N for netflix or menu navigation commands to get to a particular menu.
const channelFavoriteLookup = {
    '-': 'ADVANCE',
    '.': 'ADVANCE',
    '0': 'NUM0',
    '1': 'NUM1',
    '2': 'NUM2',
    '3': 'NUM3',
    '4': 'NUM4',
    '5': 'NUM5',
    '6': 'NUM6',
    '7': 'NUM7',
    '8': 'NUM8',
    '9': 'NUM9',
    D: 'DOWN',
    L: 'LEFT',
    M: 'TIVO',
    N: 'NETFLIX',
    R: 'RIGHT',
    S: 'SELECT',
    T: 'LIVETV',
    U: 'UP',
    X: 'EXIT',
};
export class Tivo {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.liveTvMode = false;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.channelKeyboardType = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () { return 'numericWithDash'; }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (value.operation === 'channelSet') {
                    // Enter the channels manually since SETCH or FORCECH doesn't work.
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                    if (value.channel.toLowerCase() === 'netflix') {
                        yield this.socket.sendAsync(`KEYBOARD NETFLIX`);
                        this.liveTvMode = false;
                    }
                    else if (value.channel.toLowerCase() === 'livetv') {
                        yield this.socket.send(`TELEPORT LIVETV`, /LIVETV_READY|ERROR/i, [
                            'set.LIVETV',
                        ]);
                        this.liveTvMode = true;
                    }
                    else {
                        if (this.isDigitsAndDash(value.channel) && !this.liveTvMode) {
                            // If changing to a channel and not in live TV mode, then change to Live TV
                            // mode and wait 800 msec.
                            yield this.socket.sendAsync(`KEYBOARD LIVETV`);
                            this.liveTvMode = true;
                            yield this.timeouts.delay(800);
                        }
                        for (const ch of value.channel) {
                            if (channelFavoriteLookup.hasOwnProperty(ch)) {
                                const chan = channelFavoriteLookup[ch];
                                if (chan.toLowerCase() === 'livetv' && !this.liveTvMode) {
                                    // If we don't need to set Live TV mode because we are already in
                                    // that mode, don't set it again as it takes several seconds to
                                    // change.
                                    yield this.socket.sendAsync(`KEYBOARD ${chan}`);
                                    this.liveTvMode = true;
                                }
                                else {
                                    yield this.socket.sendAsync(`KEYBOARD ${chan}`);
                                }
                            }
                            else {
                                yield this.timeouts.delay(250); // an unknown key causes a pause
                            }
                        }
                    }
                }
                else if (keyboardControls.hasOwnProperty(value.operation)) {
                    const operation = (_a = keyboardControls[value.operation]) !== null && _a !== void 0 ? _a : '';
                    yield this.socket.sendAsync(`KEYBOARD ${operation}`);
                    this.liveTvMode = false;
                }
                else {
                    this.log.error(`unhandled media operation: ${value.operation}`);
                }
                return value;
            }),
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                const mediaOperationCaps = {};
                const mediaOperations = [
                    'back',
                    'channelSet',
                    'channelUpDown',
                    'closedCaptioningSet',
                    'directionUpDownLeftRight',
                    'directionEnter',
                    'exit',
                    'guide',
                    'info',
                    'keyDash',
                    'keyDigits',
                    'keyEnter',
                    'menu',
                    'mediaPlayerForward',
                    'mediaPlayerPause',
                    'mediaPlayerPlay',
                    'mediaPlayerRecord',
                    'mediaPlayerReverse',
                    'mediaPlayerSkipAhead',
                    'mediaPlayerSkipBack',
                    'mediaPlayerStop',
                    'pageUpDown',
                ];
                for (const input of Object.values((_c = (_b = this.config) === null || _b === void 0 ? void 0 : _b.videoInputs) !== null && _c !== void 0 ? _c : {})) {
                    const tvTunerCode = '1';
                    if (input.code === tvTunerCode) {
                        mediaOperationCaps[input.code] = [...mediaOperations];
                    }
                }
                return mediaOperationCaps;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.send('TIVO', /INVALID_COMMAND/i, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.config = config;
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: 'TIVO',
                    next: /INVALID_COMMAND|CH_STATUS/i,
                },
            ], credentials);
        });
    }
    isDigitsAndDash(channel) {
        const chanRegExp = new RegExp(/^\d+-?\.?\d*$/);
        return chanRegExp.test(channel);
    }
}
Tivo.driverType = 'socketDriver';
Tivo.key = '-NPZG9Lr3DO8F9BtdDDB';
Tivo.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\r', '\n'],
        },
        port: 31339,
        sendTerminator: '\r',
    },
];
